import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Images, Plus, Search, Folder, ArrowRight, SlidersHorizontal,
  CalendarDays, Tag, Heart,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminGalleries() {
  const [clients, setClients] = useState([]);
  const [orphanGalleries, setOrphanGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        api.get("/admin/gallery-clients"),
        api.get("/admin/galleries"),
      ]);
      setClients(c.data);
      // Galleries without a client_id are shown in a separate "Unassigned" group
      setOrphanGalleries((g.data || []).filter((x) => !x.client_id));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Aggregate category tags across all galleries
  const allTags = useMemo(() => {
    const set = new Set();
    clients.forEach((c) => (c.latest_collections || []).forEach((g) => (g.category_tags || []).forEach((t) => set.add(t))));
    orphanGalleries.forEach((g) => (g.category_tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [clients, orphanGalleries]);

  const filteredClients = useMemo(() => {
    let list = [...clients];
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((c) =>
        c.name.toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s) ||
        (c.latest_collections || []).some((g) => g.name.toLowerCase().includes(s))
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((c) => (c.latest_collections || []).some((g) => g.status === statusFilter));
    }
    if (tagFilter !== "all") {
      list = list.filter((c) => (c.latest_collections || []).some((g) => (g.category_tags || []).includes(tagFilter)));
    }
    if (sortBy === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => new Date(b.latest_created_at || 0) - new Date(a.latest_created_at || 0));
    return list;
  }, [clients, search, statusFilter, tagFilter, sortBy]);

  return (
    <div className="space-y-8" data-testid="admin-galleries-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Client galleries</div>
          <h1 className="font-serif text-4xl font-light">Collections</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Group every shoot, edit, and final delivery for a client in one place. Each client can hold multiple collections.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90"
          data-testid="new-collection-btn"
        >
          <Plus className="w-4 h-4" /> New collection
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 border-b border-border pb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients or collections…"
            className="w-full pl-9 pr-3 h-10 bg-transparent border border-border outline-none focus:border-foreground text-sm"
            data-testid="gallery-search"
          />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <FilterSelect value={statusFilter} onChange={setStatusFilter} testid="filter-status" options={[
            ["all", "All status"], ["draft", "Draft"], ["sent", "Sent"], ["selections_received", "Selections received"], ["delivered", "Delivered"],
          ]} />
          {allTags.length > 0 && (
            <FilterSelect value={tagFilter} onChange={setTagFilter} testid="filter-tag" options={[["all", "All tags"], ...allTags.map((t) => [t, t])]} />
          )}
          <FilterSelect value={sortBy} onChange={setSortBy} testid="sort-by" options={[
            ["recent", "Most recent"], ["name", "Client A–Z"],
          ]} icon={<SlidersHorizontal className="w-3.5 h-3.5" />} />
        </div>
      </div>

      {loading && <div className="text-muted-foreground">Loading…</div>}

      {!loading && filteredClients.length === 0 && orphanGalleries.length === 0 && (
        <div className="border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="empty-galleries">
          <Images className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" />
          <div className="font-serif text-2xl text-foreground/60">No collections yet</div>
          <div className="text-sm mt-1">Click &quot;New collection&quot; to create your first client folder + gallery.</div>
        </div>
      )}

      {/* Client cards */}
      {filteredClients.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="client-grid">
          {filteredClients.map((c) => (
            <ClientCard key={c.id} client={c} onClick={() => navigate(`/admin/galleries/clients/${c.id}`)} />
          ))}
        </div>
      )}

      {/* Unassigned galleries */}
      {orphanGalleries.length > 0 && (
        <div className="space-y-3 pt-4">
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif text-xl">Unassigned collections</h2>
            <span className="text-xs text-muted-foreground">{orphanGalleries.length}</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {orphanGalleries.map((g) => (
              <button key={g.id} onClick={() => navigate(`/admin/galleries/${g.id}`)} className="text-left bg-card border border-border p-3 hover:border-foreground/60 relative" data-testid={`orphan-gallery-${g.id}`}>
                {(g.selection_count || 0) > 0 && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 bg-rose-500 text-white text-[10px] uppercase tracking-[0.15em] z-10" data-testid={`orphan-selections-badge-${g.id}`}>
                    <Heart className="w-3 h-3 fill-white" /> {g.selection_count}
                  </span>
                )}
                <div className="aspect-[4/3] bg-muted mb-3 overflow-hidden">
                  {g.cover_image && <img src={`${BACKEND}${g.cover_image}`} alt={g.name} className="w-full h-full object-cover" />}
                </div>
                <div className="font-serif">{g.name}</div>
                <div className="text-xs text-muted-foreground">{g.customer_name || "—"}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <NewCollectionDialog open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} clients={clients} />
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid, icon }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 px-3 text-xs uppercase tracking-[0.15em] gap-2 min-w-[140px]" data-testid={testid}>
        {icon}
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function ClientCard({ client, onClick }) {
  const cols = client.latest_collections || [];
  const slots = [...cols.slice(0, 4)];
  while (slots.length < 4) slots.push(null);
  // Surface total selections waiting for review across all of this client's galleries —
  // mirrors the rose heart-badge studio owners are taught to look for.
  const totalSelections = cols.reduce((n, g) => n + (g?.selection_count || 0), 0);
  return (
    <button onClick={onClick} className="text-left bg-card border border-border p-4 hover:border-foreground/60 transition-colors group relative" data-testid={`client-card-${client.id}`}>
      {totalSelections > 0 && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 bg-rose-500 text-white text-[10px] uppercase tracking-[0.15em] z-10" data-testid={`client-selections-badge-${client.id}`}>
          <Heart className="w-3 h-3 fill-white" /> {totalSelections}
        </span>
      )}
      <div className="grid grid-cols-2 gap-1 aspect-square mb-4 bg-muted">
        {slots.map((g, i) => (
          <div key={`${client.id}-slot-${i}`} className="bg-stone-100 overflow-hidden">
            {g?.cover_image && (
              <img src={`${BACKEND}${g.cover_image}`} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-1">
        <Folder className="w-4 h-4 text-muted-foreground shrink-0" />
        <div className="font-serif text-lg truncate">{client.name}</div>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{client.collection_count} collection{client.collection_count === 1 ? "" : "s"}</span>
        {client.event_date && <><span>·</span><span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{new Date(client.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span></>}
        <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </button>
  );
}

function NewCollectionDialog({ open, onClose, onCreated, clients }) {
  const navigate = useNavigate();
  const [clientMode, setClientMode] = useState("new"); // 'new' | 'existing'
  const [clientId, setClientId] = useState("");
  const [clientDraft, setClientDraft] = useState({ name: "", email: "", phone: "", event_date: "" });
  const [name, setName] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [adv, setAdv] = useState(() => loadAdvancedDefaults());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setClientMode("new"); setClientId("");
      setClientDraft({ name: "", email: "", phone: "", event_date: "" });
      setName("");
      setShowAdvanced(false);
      setAdv(loadAdvancedDefaults());
      setSubmitting(false);
    }
  }, [open]);

  async function submit() {
    if (submitting) return;
    if (!name.trim()) return toast.error("Collection name is required");
    setSubmitting(true);
    try {
      // 1) resolve client
      let cid = clientId;
      let clientName = "", clientEmail = "";
      if (clientMode === "new") {
        if (!clientDraft.name.trim()) { setSubmitting(false); return toast.error("Client name is required"); }
        const { data: created } = await api.post("/admin/gallery-clients", {
          name: clientDraft.name.trim(),
          email: clientDraft.email || undefined,
          phone: clientDraft.phone || undefined,
          event_date: clientDraft.event_date || undefined,
        });
        cid = created.id;
        clientName = created.name; clientEmail = created.email || "";
      } else {
        if (!cid) { setSubmitting(false); return toast.error("Pick a client"); }
        const existing = clients.find((c) => c.id === cid);
        clientName = existing?.name || ""; clientEmail = existing?.email || "";
      }
      // 2) create gallery — only `name` and `client_id` are required; everything else optional
      const tags = (adv.category_tags || "").split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/galleries", {
        name: name.trim(),
        client_id: cid,
        customer_name: clientName,
        customer_email: clientEmail || undefined,
        password: adv.password.trim() || undefined,
        download_password: adv.download_password.trim() || undefined,
        included_edits: Number(adv.included_edits) || 0,
        extra_edit_price: Number(adv.extra_edit_price) || 35,
        max_selections: Number(adv.max_selections) || 0,
        welcome_message: adv.welcome_message || undefined,
        cover_layout: adv.cover_layout,
        category_tags: tags,
      });
      saveAdvancedDefaults(adv); // remember for next time
      toast.success("Collection created");
      onCreated();
      navigate(`/admin/galleries/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">New collection</DialogTitle>
        </DialogHeader>

        <div className="space-y-5" data-testid="new-collection-form">
          {/* Client */}
          <div className="space-y-3">
            <div className="eyebrow">Client</div>
            <div className="flex gap-2">
              <Tab active={clientMode === "new"} onClick={() => setClientMode("new")} testid="tab-new-client">New client</Tab>
              <Tab active={clientMode === "existing"} onClick={() => setClientMode("existing")} testid="tab-existing-client" disabled={clients.length === 0}>Existing client</Tab>
            </div>

            {clientMode === "new" ? (
              <div className="space-y-3">
                <In label="Client name *" value={clientDraft.name} onChange={(v) => setClientDraft({ ...clientDraft, name: v })} testid="client-name" placeholder="e.g. Oyinda Agarah" />
                <div className="grid grid-cols-2 gap-3">
                  <In label="Email (optional)" value={clientDraft.email} onChange={(v) => setClientDraft({ ...clientDraft, email: v })} testid="client-email" />
                  <In label="Event date (optional)" type="date" value={clientDraft.event_date} onChange={(v) => setClientDraft({ ...clientDraft, event_date: v })} testid="client-event-date" />
                </div>
              </div>
            ) : (
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="existing-client-select"><SelectValue placeholder="Pick a client…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Collection name — the only other required field */}
          <div>
            <In label="Collection name *" value={name} onChange={setName} testid="collection-name" placeholder="e.g. Spring Maternity" />
            <p className="text-[11px] text-muted-foreground mt-1.5">
              That&apos;s it — you can upload photos right away. No password by default; clients open the link directly.
            </p>
          </div>

          {/* Advanced (optional) */}
          <div className="border-t border-border pt-4">
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground flex items-center gap-1.5"
              data-testid="toggle-advanced-btn"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {showAdvanced ? "Hide" : "Show"} advanced options
              <span className="text-[10px] opacity-60">(optional)</span>
            </button>

            {showAdvanced && (
              <div className="space-y-4 mt-4 bg-muted/30 p-4" data-testid="advanced-section">
                <div className="grid grid-cols-2 gap-3">
                  <In label="View password" value={adv.password} onChange={(v) => setAdv({ ...adv, password: v })} testid="collection-password" placeholder="Optional" />
                  <In label="Download password" value={adv.download_password} onChange={(v) => setAdv({ ...adv, download_password: v })} testid="collection-download-password" placeholder="Optional" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <In label="Included edits" type="number" value={adv.included_edits} onChange={(v) => setAdv({ ...adv, included_edits: v })} testid="collection-included-edits" />
                  <In label="Extra edit ($)" type="number" value={adv.extra_edit_price} onChange={(v) => setAdv({ ...adv, extra_edit_price: v })} testid="collection-extra-price" />
                  <In label="Max selections" type="number" value={adv.max_selections} onChange={(v) => setAdv({ ...adv, max_selections: v })} testid="collection-max-sel" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="eyebrow">Cover layout</span>
                    <Select value={adv.cover_layout} onValueChange={(v) => setAdv({ ...adv, cover_layout: v })}>
                      <SelectTrigger className="mt-1" data-testid="collection-cover-layout"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["center", "left", "novel", "vintage", "frame", "stripe", "divider", "journal"].map((l) =>
                          <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </label>
                  <In label="Category tags (comma-sep)" value={adv.category_tags} onChange={(v) => setAdv({ ...adv, category_tags: v })} testid="collection-tags" placeholder="maternity, family" />
                </div>
                <label className="block">
                  <span className="eyebrow">Welcome message</span>
                  <textarea
                    rows={2}
                    value={adv.welcome_message}
                    onChange={(e) => setAdv({ ...adv, welcome_message: e.target.value })}
                    className="mt-1 w-full bg-transparent border border-border p-2 outline-none focus:border-foreground text-sm"
                    data-testid="collection-welcome"
                  />
                </label>
                <p className="text-[11px] text-muted-foreground italic">
                  These settings are remembered for next time. You can change any of them later in the collection&apos;s Settings tab.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <button onClick={onClose} className="px-5 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60">Cancel</button>
            <button
              onClick={submit}
              disabled={submitting}
              className="px-5 h-10 text-xs uppercase tracking-[0.18em] bg-primary text-primary-foreground disabled:opacity-60"
              data-testid="create-collection-btn"
            >
              {submitting ? "Creating…" : "Create collection"}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// localStorage helpers for remembering last-used advanced settings
const ADV_KEY = "allure_collection_adv_defaults";
function loadAdvancedDefaults() {
  try {
    const raw = localStorage.getItem(ADV_KEY);
    if (raw) return { ...DEFAULT_ADV, ...JSON.parse(raw) };
  } catch (err) { console.warn("adv defaults load failed", err); }
  return { ...DEFAULT_ADV };
}
function saveAdvancedDefaults(adv) {
  try { localStorage.setItem(ADV_KEY, JSON.stringify(adv)); } catch (err) { console.warn("adv defaults save failed", err); }
}
const DEFAULT_ADV = {
  password: "", download_password: "", included_edits: 0, extra_edit_price: 35,
  max_selections: 0, welcome_message: "", category_tags: "", cover_layout: "center",
};

function Tab({ active, onClick, children, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 h-9 text-xs uppercase tracking-[0.18em] border ${active ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/60"} disabled:opacity-40 disabled:cursor-not-allowed`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function In({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        data-testid={testid}
      />
    </label>
  );
}
